import {IntlShape, useIntl} from 'react-intl';
import {notNullishAndPredicate} from '../../util/NotNullPredicate';
import {ProjectDropdown_viewer$data} from './__generated__/ProjectDropdown_viewer.graphql';
import {
	DELIBERATE_SCHEDULING_ORDER,
	inProfileGroupOrder,
	stageIdOf,
	unknownStagesOf,
	stageLabel,
	StageProfile,
} from '../../util/projectStageUtil';
import {visibleStages} from '../../util/opportunityStages';
import {useProjectStages} from '../../hooks/useProjectStages';

// The deliberate scheduling order up to Opportunity: Halted and Done projects are not offered here.
const TASK_MODAL_ORDER: StageProfile[] = DELIBERATE_SCHEDULING_ORDER.slice(0, 3);

type ProjectEdges = NonNullable<NonNullable<ProjectDropdown_viewer$data['projects']>['edges']>;
type ProjectNode = NonNullable<NonNullable<ProjectEdges[0]>['node']>;
type ProjectGroupEdges = NonNullable<NonNullable<ProjectDropdown_viewer$data['projectGroups']>['edges']>;
type ProjectGroupNode = NonNullable<NonNullable<ProjectGroupEdges[0]>['node']>;

type ProjectDropdownItem_projectGroup = {
	color: string;
	name: string;
	label: string;
	searchString: string;
	companyProjectGroupId: number;
	isProjectGroup: boolean;
	sortValue: string;
};

type ProjectDropdownItem_project = {
	id: string;
	projectColor: string;
	name: string;
	label: string;
	searchString: string;
	companyProjectId: number;
	customProjectId: string;
	projectGroupId?: string;
	isInProjectGroup?: boolean;
	sortValue: string | number;
};

type ProjectDropdownItem = ProjectDropdownItem_project | ProjectDropdownItem_projectGroup;

type SortableDropdownItem = {sortValue: string | number; isInProjectGroup?: boolean};

type ProjectOption = {
	value: string;
	label: string;
	nestedOptions: ProjectDropdownItem[];
};

type ProjectDropdownData = {
	selectedProjectLabel: string;
	projectOptions: ProjectOption[];
};

// Replace with relay type once task modal is ported to TS. Should be defined as a ProjectDropdown_project fragment as well.
export type SelectedProject = {
	name?: string;
	client?: {name?: string};
	companyProjectId?: number;
	customProjectId?: string;
	projectColor?: string;
	isProjectGroup?: boolean;
};

// In order to ensure that the connected project parent is always shown when a connected project child is shown, the searchstring for a connected project contains the concatenation of all searchstrings from its children.
function getSearchStringForProjectGroup(
	projectGroupName: string | null,
	companyProjectGroupId: number | null,
	projectGroupProjects: ProjectDropdownItem[]
) {
	const projectGroupProjectsSearchStrings = projectGroupProjects.map(project => project.searchString).join(' ');
	return `${projectGroupName ?? ''} X${companyProjectGroupId ?? ''} ${projectGroupProjectsSearchStrings}`;
}

function projectNodeToProjectDropdownItem(project: ProjectNode, sortValue?: string): ProjectDropdownItem_project {
	return {
		id: project.id,
		projectColor: project.projectColor ?? '',
		name: project.name ?? '',
		label: project.name ?? '',
		searchString: `${project.name ?? ''} ${project.customProjectId ?? ''}`,
		companyProjectId: project.companyProjectId ?? -1,
		customProjectId: project.customProjectId ?? '',
		projectGroupId: project.projectGroupId ?? undefined,
		isInProjectGroup: !!project.projectGroupId,
		sortValue: sortValue ?? project.companyProjectId ?? -1,
	};
}

function projectGroupNodeToProjectDropdownItem(
	projectGroup: ProjectGroupNode,
	searchString: string
): ProjectDropdownItem_projectGroup {
	return {
		color: projectGroup.color ?? '',
		name: projectGroup.name ?? '',
		label: projectGroup.name ?? '',
		searchString,
		companyProjectGroupId: projectGroup.companyProjectGroupId ?? -1,
		isProjectGroup: true,
		sortValue: projectGroup.companyProjectGroupId + '_group',
	};
}

function getProjectItemsForStageIds(
	projects: ProjectEdges,
	stageIds: string[],
	externalSortValue?: string
): ProjectDropdownItem[] {
	return projects
		.map(project => project?.node)
		.filter(notNullishAndPredicate(project => stageIds.includes(stageIdOf(project) ?? '')))
		.map(project => projectNodeToProjectDropdownItem(project, externalSortValue));
}

function getProjectGroupNodeItemsForStageIds(projectGroupNode: ProjectGroupNode, stageIds: string[]): ProjectDropdownItem[] {
	const projectGroupProjectItemsForStatus = projectGroupNode.projects?.edges
		? getProjectItemsForStageIds(
				projectGroupNode.projects.edges,
				stageIds,
				projectGroupNode.companyProjectGroupId + '_group'
		  )
		: [];

	if (projectGroupProjectItemsForStatus.length === 0) return [];

	const searchString = getSearchStringForProjectGroup(
		projectGroupNode.name,
		projectGroupNode.companyProjectGroupId,
		projectGroupProjectItemsForStatus
	);
	const projectGroupItem = projectGroupNodeToProjectDropdownItem(projectGroupNode, searchString);

	return [projectGroupItem, ...projectGroupProjectItemsForStatus];
}

function getProjectGroupItemsForStageIds(projectGroups: ProjectGroupEdges, stageIds: string[]): ProjectDropdownItem[] {
	return projectGroups
		.map(projectGroup => projectGroup?.node)
		.filter(notNullishAndPredicate())
		.flatMap(projectGroup => getProjectGroupNodeItemsForStageIds(projectGroup, stageIds));
}

function getDropdownItemsForStageIds(
	projects: ProjectEdges,
	projectGroups: ProjectGroupEdges,
	stageIds: string[]
): ProjectDropdownItem[] {
	const projectItemsForStatus = getProjectItemsForStageIds(projects, stageIds);
	const projectGroupItemsForStatus = getProjectGroupItemsForStageIds(projectGroups, stageIds);

	return [...projectItemsForStatus, ...projectGroupItemsForStatus];
}

function getProjectOptions(
	viewer: ProjectDropdown_viewer$data,
	stages: ReadonlyArray<{id: string; name?: string | null; profile?: string | null}>,
	intl: IntlShape
): ProjectOption[] {
	const projects = viewer.projects?.edges ?? [];
	const projectGroups = viewer.projectGroups?.edges ?? [];

	const sortProjects = (a: SortableDropdownItem, b: SortableDropdownItem): number => {
		if (a.sortValue < b.sortValue) return -1;
		if (a.sortValue > b.sortValue) return 1;
		if (a.sortValue === b.sortValue) {
			if (a.isInProjectGroup && !b.isInProjectGroup) {
				return 1;
			}
		}
		return 0;
	};

	return stages.map(stage => ({
		value: stage.id,
		label: stageLabel(stage, intl),
		nestedOptions: getDropdownItemsForStageIds(projects, projectGroups, [stage.id]).sort(sortProjects),
	}));
}

export const useProjectDropdownController = (
	viewer: ProjectDropdown_viewer$data,
	showOpportunity: boolean, // Supplied as a prop since it's loaded from session storage, meaning it is hard to mock for testing
	selectedProject?: SelectedProject
): ProjectDropdownData => {
	const intl = useIntl();
	const companyStages = useProjectStages();

	const viewerProjects = [
		...(viewer.projects?.edges ?? []).map(edge => edge?.node),
		...(viewer.projectGroups?.edges ?? []).flatMap(group => group?.node?.projects?.edges?.map(edge => edge?.node) ?? []),
	].filter(notNullishAndPredicate());
	const projectStageIds = viewerProjects.flatMap(project => stageIdOf(project) ?? []);

	// An Opportunity stage hidden by the rule stays when it has projects, so they stay reachable.
	// Projects whose stage is not in the store still get a trailing section, so they stay reachable.
	const stages = [
		...visibleStages(inProfileGroupOrder(companyStages, TASK_MODAL_ORDER), projectStageIds, showOpportunity),
		...unknownStagesOf(viewerProjects, companyStages),
	];
	const projectOptions = getProjectOptions(viewer, stages, intl);
	const selectedProjectLabel = selectedProject?.client?.name ?? intl.formatMessage({id: 'common.project'});
	return {selectedProjectLabel, projectOptions};
};
